import { useCallback, useMemo, useState } from 'react';
import { useBulkConversationContext } from 'src/graphql/entities/conversation';
import {
  ConversationMarkdownSourceConversation,
  convertConversationContextToMarkdown,
} from 'src/pages/Conversations/markdown';
import { useCopyToClipboard, useNotifications, useViewerTimezone } from 'src/utils/hooks';

const BULK_MARKDOWN_SEPARATOR = '\n\n- -\n\n';
const MAX_BULK_MARKDOWN_COPY_ITEMS = 120;
const MIN_BULK_MARKDOWN_LOADING_MS = 2000;

export interface BulkConversationMarkdownInput {
  conversations: ConversationMarkdownSourceConversation[];
  getConversationUrl: (conversation: ConversationMarkdownSourceConversation) => string;
}

export const useBulkCopyConversationAsMarkdown = () => {
  const { showErrorAlert, showInfoAlert } = useNotifications();
  const { copyToClipboard } = useCopyToClipboard({
    successMessage: 'Conversations markdown copied.',
  });
  const { getBulkConversationContext, loading } = useBulkConversationContext();
  const [isCopying, setIsCopying] = useState(false);
  const viewerTimezoneName = useViewerTimezone();

  const copyConversationsAsMarkdown = useCallback(
    async ({ conversations, getConversationUrl }: BulkConversationMarkdownInput) => {
      if (!conversations.length || isCopying || loading) {
        return;
      }

      if (conversations.length > MAX_BULK_MARKDOWN_COPY_ITEMS) {
        showInfoAlert(
          `You can copy up to ${MAX_BULK_MARKDOWN_COPY_ITEMS} conversations as markdown at once. Please select fewer items.`,
        );
        return;
      }

      setIsCopying(true);
      const startedAt = Date.now();

      try {
        const conversationIds = conversations.map(
          (conversation) => conversation.conversationId,
        );
        const partnerId = conversations[0]?.partnerId ?? undefined;
        const response = await getBulkConversationContext({
          conversationIds,
          partnerId,
        });
        const results = response?.data?.getBulkConversationContext?.results ?? [];
        const resultByConversationId = new Map(
          results.map((result) => [result.conversationId, result]),
        );

        const blocks = conversations
          .map((conversation) => {
            const result = resultByConversationId.get(conversation.conversationId);

            if (!result?.context || result.error) {
              return '';
            }

            return convertConversationContextToMarkdown({
              conversation,
              context: result.context,
              conversationUrl: getConversationUrl(conversation),
              viewerTimezoneName,
            });
          })
          .filter((block) => block.trim().length > 0);

        if (!blocks.length) {
          throw new Error('Conversation markdown export is empty.');
        }

        await copyToClipboard(blocks.join(BULK_MARKDOWN_SEPARATOR));

        const failedCount = results.filter((result) => result.error || !result.context).length;
        if (failedCount > 0) {
          showErrorAlert('Some conversations could not be exported to markdown.');
        }
      } catch {
        showErrorAlert('Failed to copy conversations as markdown. Please try again.');
      } finally {
        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs < MIN_BULK_MARKDOWN_LOADING_MS) {
          await new Promise((resolve) =>
            setTimeout(resolve, MIN_BULK_MARKDOWN_LOADING_MS - elapsedMs),
          );
        }
        setIsCopying(false);
      }
    },
    [
      copyToClipboard,
      getBulkConversationContext,
      isCopying,
      loading,
      showErrorAlert,
      showInfoAlert,
      viewerTimezoneName,
    ],
  );

  const isCopyConversationsAsMarkdownLoading = useMemo(
    () => isCopying || loading,
    [isCopying, loading],
  );

  return {
    copyConversationsAsMarkdown,
    isCopyConversationsAsMarkdownLoading,
  };
};
